import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import { REGISTRATION_OPEN, DCC_DOWNLOAD_URL, DCC_REQUIREMENT } from './registrationConfig';
import { DCC_INSTALL_STEPS, DCC_SMARTSCREEN_IMG, DCC_SMARTSCREEN_CAPTION } from './dccInstallGuide';
import {
  dccContinuationToken,
  registrationGateDestination,
  safeDccContinuation,
  withDccContinuation,
} from './authContinuation';

// VISINVOKE customer registration page.
//
// Website is the account authority. This component collects the required account
// fields and calls the EXISTING AuthContext.register(), which posts to the EXISTING
// backend endpoint POST /v1/auth/register. No new account system, no backend logic is
// duplicated here — the backend remains authoritative for all rules (uniqueness,
// password policy, verification email, session/cookie).
//
// The Desktop Command (DCC) opt-in is saved AGAINST THE ACCOUNT (dcc_selected) via the
// register call, so onboarding state is per-account (not per-browser) and readable on
// any device via /v1/auth/me. On successful creation the download is initiated if the
// customer opted in and an installer URL exists (the download event is recorded against
// the account), then the customer is taken directly to the VISINVOKE dashboard where a
// one-time DCC verification overlay guides them. DCC itself authenticates later through
// the existing PKCE browser handoff (unchanged).
//
// UI/content note: the DCC block now includes an expandable "what happens when you
// install" panel (purpose recap, the quick install steps, and the Windows confirmation
// screen image) so the customer knows what to expect BEFORE continuing. This is purely
// informational — the account logic, the register() call, the dcc_selected opt-in, and
// the post-create download/redirect are all unchanged.

export default function Register() {
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [wantsDcc, setWantsDcc]     = useState(true);
  const [showDccHelp, setShowDccHelp] = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const redirect = new URLSearchParams(window.location.search).get('redirect');
  const dccContinuation = safeDccContinuation(redirect);
  const continuationToken = dccContinuationToken(dccContinuation);
  const closedDestination = registrationGateDestination(REGISTRATION_OPEN, dccContinuation);

  // Launch gate: if registration is not open, never render the signup UI.
  if (closedDestination) {
    return <Navigate to={closedDestination} replace />;
  }

  const validate = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password || !confirm) {
      return 'Please fill in all required fields.';
    }
    // Mirror the backend's simple email shape check for fast feedback only.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return 'Please enter a valid email address.';
    }
    // Mirror the backend minimum (>= 8). The backend remains authoritative.
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    if (password !== confirm) {
      return 'Passwords do not match.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    const clientError = validate();
    if (clientError) { setError(clientError); return; }
    setLoading(true);
    try {
      // Save the DCC opt-in against the account (dcc_selected) via the existing register.
      await register(email.trim(), password, firstName.trim(), lastName.trim(), undefined, wantsDcc, continuationToken);

      // Registration set the website cookie, so resume the signed authorize transaction directly.
      if (dccContinuation) { window.location.assign(dccContinuation); return; }

      // If the customer opted in AND a real installer URL exists, initiate the download
      // now and record the download event against the account. When no installer URL
      // exists yet, nothing is downloaded and the dashboard overlay routes them to the
      // DCC page (PENDING).
      if (wantsDcc && DCC_DOWNLOAD_URL) {
        try { window.open(DCC_DOWNLOAD_URL, '_blank', 'noopener'); } catch (e) {}
        try { await API.post('/v1/auth/dcc-downloaded'); } catch (e) {}
      }

      // Automatic dashboard entry — DCC guidance happens on the dashboard, not here.
      navigate('/dashboard');
    } catch (err) {
      // Backend is authoritative: surface its message (e.g. duplicate email 409).
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // --- Registration form -------------------------------------------------------
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Visinvoke</div>
        <h2 style={styles.title}>Create VISINVOKE Account</h2>
        <p style={styles.lead}>
          A VISINVOKE account is required to access all VISINVOKE services.
        </p>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>First name</label>
              <input style={styles.input} type="text" value={firstName}
                onChange={e => setFirstName(e.target.value)} placeholder="Jane" autoComplete="given-name" required />
            </div>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Last name</label>
              <input style={styles.input} type="text" value={lastName}
                onChange={e => setLastName(e.target.value)} placeholder="Doe" autoComplete="family-name" required />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" value={password}
              onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm password</label>
            <input style={styles.input} type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)} placeholder="Re-enter your password" autoComplete="new-password" required />
          </div>

          <div style={styles.dccBox}>
            <div style={styles.dccHeading}>{DCC_REQUIREMENT.option}</div>
            <p style={styles.dccText}>{DCC_REQUIREMENT.explanation}</p>
            <ul style={styles.dccList}>
              {DCC_REQUIREMENT.points.map((p, i) => <li key={i} style={styles.dccPoint}>{p}</li>)}
            </ul>

            <label style={styles.checkRow}>
              <input type="checkbox" checked={wantsDcc} onChange={e => setWantsDcc(e.target.checked)} />
              <span style={styles.checkLabel}>{DCC_REQUIREMENT.downloadOptionLabel}</span>
            </label>

            {/* Expandable "what happens when you install" — purely informational */}
            <button
              type="button"
              style={styles.dccHelpToggle}
              onClick={() => setShowDccHelp(v => !v)}
              aria-expanded={showDccHelp}
            >
              {showDccHelp ? 'Hide what happens next' : 'What happens when you install?'}
            </button>

            {showDccHelp && (
              <div style={styles.dccHelp}>
                <p style={styles.dccHelpLead}>
                  After your account is created, Desktop Command Centre downloads automatically. Open the
                  installer and follow these steps:
                </p>
                <ol style={styles.dccSteps}>
                  {DCC_INSTALL_STEPS.map((step, i) => <li key={i} style={styles.dccStep}>{step}</li>)}
                </ol>
                <img src={DCC_SMARTSCREEN_IMG} alt="Windows installation confirmation screen" style={styles.dccShot} />
                <p style={styles.dccCaption}>{DCC_SMARTSCREEN_CAPTION}</p>
              </div>
            )}
          </div>

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Creating your account...' : 'Create Account'}
          </button>
        </form>
        <p style={styles.altLine}>
          Already have a VISINVOKE account?{' '}
          <span style={styles.altLink} onClick={() => navigate(withDccContinuation('/login', dccContinuation))}>
            Sign in
          </span>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #334155, #881337)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { background: '#fff', borderRadius: 12, padding: '40px 36px', width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  logo: { fontWeight: 900, fontSize: 22, color: '#8b5cf6', marginBottom: 8 },
  title: { margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#1a1a1a' },
  lead: { margin: '0 0 20px', fontSize: 13, color: '#666', lineHeight: 1.6 },
  error: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  row: { display: 'flex', gap: 12 },
  field: { marginBottom: 16 },
  label: { display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 },
  input: { width: '100%', border: '2px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' },
  dccBox: { background: '#f7f5ff', border: '1px solid #e6ddff', borderRadius: 10, padding: '16px 16px 14px', margin: '4px 0 20px' },
  dccHeading: { fontWeight: 700, fontSize: 14, color: '#5b21b6', marginBottom: 6 },
  dccText: { fontSize: 13, color: '#4b4b57', lineHeight: 1.55, margin: '0 0 10px' },
  dccList: { margin: '0 0 12px', paddingLeft: 18 },
  dccPoint: { fontSize: 12.5, color: '#555', lineHeight: 1.6 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none' },
  checkLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  dccHelpToggle: { display: 'inline-block', marginTop: 12, background: 'none', border: 'none', color: '#8b5cf6', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  dccHelp: { marginTop: 12, background: '#fff', border: '1px solid #e6ddff', borderRadius: 8, padding: '14px 14px 12px' },
  dccHelpLead: { fontSize: 12.5, color: '#4b4b57', lineHeight: 1.6, margin: '0 0 10px' },
  dccSteps: { margin: '0 0 12px', paddingLeft: 18 },
  dccStep: { fontSize: 12.5, color: '#4b4b57', lineHeight: 1.7, marginBottom: 2 },
  dccShot: { display: 'block', width: '100%', height: 'auto', borderRadius: 8, border: '1px solid #e6ddff', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
  dccCaption: { margin: '10px 0 0', fontSize: 11.5, color: '#6b6b78', lineHeight: 1.55 },
  btn: { width: '100%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 },
  altLine: { textAlign: 'center', marginTop: 18, fontSize: 13, color: '#666' },
  altLink: { color: '#8b5cf6', fontWeight: 600, cursor: 'pointer' },
};
